import { z } from 'zod'
import { JOBCARD_ACTIONS, JOBCARD_STATUSES } from '../jobcard-status.js'
import { idSchema, isoDateTimeSchema, optionalText, text } from './common.js'

/**
 * The per-jobcard timeline: a staff-only log of what is happening to the car.
 *
 * Four kinds of entry:
 *   status:    written by the server whenever the jobcard is created or moves
 *              through the state machine. Never created, edited or deleted
 *              through the timeline API — it is the audit trail.
 *   update:    a free-text note ("brakes done, waiting on paint"), optionally
 *              naming the worker (mechanic) it concerns.
 *   sent_out:  something (the whole car, or a part) left the workshop for
 *              repair, cleaning, etc. Stays "open" until a `returned` entry
 *              points at it.
 *   returned:  closes one sent_out entry.
 *
 * Never exposed on the customer page or any customer document.
 */
export const TIMELINE_KINDS = ['status', 'update', 'sent_out', 'returned'] as const
export type TimelineKind = (typeof TIMELINE_KINDS)[number]
export const timelineKindSchema = z.enum(TIMELINE_KINDS)

/** What the outside party was asked to do. */
export const OUTSOURCE_SERVICE_TYPES = ['repair', 'cleaning', 'other'] as const
export type OutsourceServiceType = (typeof OUTSOURCE_SERVICE_TYPES)[number]
export const outsourceServiceTypeSchema = z.enum(OUTSOURCE_SERVICE_TYPES)

/** One row of a jobcard's timeline, exactly as the API returns it. */
export interface TimelineEntry {
  id: string
  jobcardId: string
  kind: TimelineKind
  /** When it happened (ISO-8601 UTC). May be backdated by staff; the list is ordered by this. */
  occurredAt: string
  /** Free text. For `status` rows this is always null. */
  note: string | null

  /** status only. Null `action` with kind `status` means "jobcard created". */
  action: (typeof JOBCARD_ACTIONS)[number] | null
  /** status only: the status the jobcard entered. */
  toStatus: (typeof JOBCARD_STATUSES)[number] | null

  /** update / sent_out / returned: the mechanic this concerns. Null when not named. */
  workerId: string | null
  /** Name snapshot taken when the entry was written, so a renamed or removed worker doesn't rewrite history. */
  workerName: string | null

  /** sent_out only. */
  serviceType: OutsourceServiceType | null
  /** sent_out only: what left the workshop — "whole car", "radiator", "front seats". */
  itemSent: string | null
  /** sent_out only: free-text name of the place/company it went to. */
  vendorName: string | null
  /** sent_out only: ISO date-time it is promised back, if known. */
  expectedBackAt: string | null

  /** returned only: the sent_out entry this closes. */
  relatedEntryId: string | null

  /** Display name of the logged-in staff member who recorded it. Null for system/machine writes. */
  recordedBy: string | null
  createdAt: string
}

/** The compact form of an OPEN sent_out entry, used on jobcard list rows. */
export interface TimelineSentOutBrief {
  entryId: string
  serviceType: OutsourceServiceType
  itemSent: string
  vendorName: string
  /** When it left (the entry's occurredAt). */
  since: string
  expectedBackAt: string | null
}

export interface TimelineSummary {
  /** sent_out entries with no `returned` entry yet, oldest first. Empty when everything is in the workshop. */
  openSentOut: TimelineEntry[]
  /** Worker named on the most recent update/sent_out/returned entry that names one. */
  lastWorkerName: string | null
  /** Distinct worker names across the whole timeline, most recent first. */
  workerNames: string[]
}

export interface JobcardTimeline {
  /** Newest first (by occurredAt, then createdAt). */
  entries: TimelineEntry[]
  summary: TimelineSummary
}

/**
 * Staff-writable input. `occurredAt` omitted means "now". Status entries are
 * deliberately not in this union.
 */
const when = isoDateTimeSchema.optional()

export const timelineUpdateInputSchema = z.object({
  kind: z.literal('update'),
  note: text(2000),
  workerId: idSchema.nullable().default(null),
  occurredAt: when,
})

export const timelineSentOutInputSchema = z.object({
  kind: z.literal('sent_out'),
  serviceType: outsourceServiceTypeSchema,
  itemSent: text(200),
  vendorName: text(120),
  expectedBackAt: isoDateTimeSchema.nullable().default(null),
  note: optionalText(2000),
  workerId: idSchema.nullable().default(null),
  occurredAt: when,
})

export const timelineReturnedInputSchema = z.object({
  kind: z.literal('returned'),
  relatedEntryId: idSchema,
  note: optionalText(2000),
  workerId: idSchema.nullable().default(null),
  occurredAt: when,
})

export const timelineEntryInputSchema = z.discriminatedUnion('kind', [
  timelineUpdateInputSchema,
  timelineSentOutInputSchema,
  timelineReturnedInputSchema,
])
export type TimelineEntryInput = z.infer<typeof timelineEntryInputSchema>

/**
 * Editing an existing update / sent_out / returned entry. Every field is
 * optional; fields that don't apply to the entry's kind are ignored (never an
 * error). `kind` and `relatedEntryId` cannot be changed.
 */
export const timelineEntryPatchSchema = z.object({
  note: z.string().trim().max(2000).nullable().optional(),
  workerId: idSchema.nullable().optional(),
  occurredAt: isoDateTimeSchema.optional(),
  serviceType: outsourceServiceTypeSchema.optional(),
  itemSent: text(200).optional(),
  vendorName: text(120).optional(),
  expectedBackAt: isoDateTimeSchema.nullable().optional(),
})
export type TimelineEntryPatch = z.infer<typeof timelineEntryPatchSchema>
